import React, { useEffect, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { CategoryScale, Chart as ChartJS, ChartData, ChartOptions, Legend, LinearScale, LineElement, PointElement, Tooltip as ChartTooltip } from 'chart.js';
import { BattlePressureLevel, PowerDieColor, SimulationLoadoutStatsOutput, WeaponMatrixCellOutput, WeaponMatrixOutput } from '../../core/types';
import { getATRangeLabel } from '../../core/utilities/primordial-at';
import { getKratosTokenColorByLabel } from '../../core/utilities/token-presentation';
import SegmentedControl from '../../components/SegmentedControl';
import './WeaponMatrixSection.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, ChartTooltip, Legend);

interface WeaponMatrixResultsProps {
    output?: WeaponMatrixOutput | null;
    isLoading?: boolean;
    placeholder?: {
        loadoutTitles: string[];
        targets: {
            bpLevel: BattlePressureLevel;
            aeonTrespassValue: number;
            aeonTrespassValues?: number[];
        }[];
    };
}

interface TokenScenarioGroup {
    label: string;
    scenarios: {
        id: string;
        step: number;
    }[];
}

function getTokenColor(label: string): string {
    return getKratosTokenColorByLabel(label);
}

function formatLoadoutTitle(loadoutIndex: number, loadoutName?: string): string {
    return loadoutName?.trim() || `Loadout ${loadoutIndex + 1}`;
}

function getCell(output: WeaponMatrixOutput, scenarioId: string, bpLevel: BattlePressureLevel): WeaponMatrixCellOutput | undefined {
    return output.cells.find((cell) => cell.scenarioId === scenarioId && cell.bpLevel === bpLevel);
}

function getLoadoutOutput(cell: WeaponMatrixCellOutput | undefined, loadoutIndex: number): SimulationLoadoutStatsOutput | undefined {
    return cell?.loadoutOutputs[loadoutIndex];
}

function simplifyDiceOutput(diceArray: PowerDieColor[]): string {
    const diceCounts: { [key in PowerDieColor]?: number } = diceArray.reduce(
        (acc, die) => {
            acc[die] = (acc[die] || 0) + 1;
            return acc;
        },
        {} as { [key in PowerDieColor]?: number },
    );

    const orderedColors: PowerDieColor[] = [PowerDieColor.RED, PowerDieColor.BLACK, PowerDieColor.WHITE, PowerDieColor.MORTAL];

    return (
        orderedColors
            .filter((color) => diceCounts[color])
            .map((color) => `${diceCounts[color]} ${color}`)
            .join(', ') || 'None'
    );
}

function getTokenScenarioGroups(output: WeaponMatrixOutput): TokenScenarioGroup[] {
    const groups: TokenScenarioGroup[] = [];

    for (const scenario of output.scenarios) {
        if (!scenario.groupLabel) continue;
        const label = scenario.groupLabel.replace(/ Impact$/, '');
        const existing = groups.find((group) => group.label === label);
        const step = parseInt(scenario.id.split('-').at(-1) || '0', 10);
        const entry = { id: scenario.id, step: isNaN(step) ? 0 : step };

        if (existing) {
            existing.scenarios.push(entry);
        } else {
            groups.push({
                label,
                scenarios: [entry],
            });
        }
    }

    return groups.map((group) => ({
        ...group,
        scenarios: group.scenarios.slice().sort((a, b) => a.step - b.step),
    }));
}

function createWoundChanceChartData(output: WeaponMatrixOutput, loadoutIndex: number, bpLevel: BattlePressureLevel, groups: TokenScenarioGroup[]): ChartData<'line'> {
    const maxStep = Math.max(0, ...groups.flatMap((group) => group.scenarios.map((scenario) => scenario.step)));
    const labels = Array.from({ length: maxStep + 1 }, (_, index) => `+${index}`);
    const baseChance = getLoadoutOutput(getCell(output, 'base', bpLevel), loadoutIndex)?.chanceToWound ?? 0;

    return {
        labels,
        datasets: groups.map((group) => {
            const scenarioByStep = new Map(group.scenarios.map((scenario) => [scenario.step, scenario.id]));
            const color = getTokenColor(group.label);
            return {
                label: group.label,
                data: labels.map((_, step) => {
                    if (step === 0) return baseChance;
                    const scenarioId = scenarioByStep.get(step);
                    if (!scenarioId) return null;
                    return getLoadoutOutput(getCell(output, scenarioId, bpLevel), loadoutIndex)?.chanceToWound ?? baseChance;
                }),
                borderColor: color,
                backgroundColor: color,
                borderWidth: 2,
                tension: 0.25,
                pointRadius: 1.5,
                spanGaps: false,
            };
        }),
    };
}

function createWoundChanceChartOptions(compactLayout: boolean): ChartOptions<'line'> {
    return {
        responsive: true,
        maintainAspectRatio: false,
        events: ['mousemove', 'mouseout', 'click'],
        layout: {
            padding: {
                bottom: compactLayout ? 16 : 12,
            },
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        scales: {
            x: {
                title: {
                    display: true,
                    text: 'Added Tokens',
                    color: '#ffffff',
                    padding: {
                        top: compactLayout ? 6 : 10,
                    },
                },
                ticks: {
                    color: '#ffffff',
                    padding: compactLayout ? 2 : 6,
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.12)',
                },
            },
            y: {
                beginAtZero: true,
                min: 0,
                max: 100,
                title: {
                    display: true,
                    text: 'Chance To Wound',
                    color: '#ffffff',
                    padding: {
                        bottom: 8,
                    },
                },
                ticks: {
                    color: '#ffffff',
                    callback: (value) => `${value}%`,
                    padding: compactLayout ? 2 : 6,
                },
                grid: {
                    color: 'rgba(255, 255, 255, 0.12)',
                },
            },
        },
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (items) => items[0]?.label || '',
                    label: (context) => {
                        const value = context.parsed.y ?? 0;
                        return `${context.dataset.label}: ${value}%`;
                    },
                },
            },
            datalabels: {
                display: false,
            } as any,
        },
        elements: {
            point: {
                hoverRadius: 4,
                hitRadius: 10,
            },
        },
    };
}

function renderTokenLegend(groups: TokenScenarioGroup[]) {
    return (
        <div className="weapon-matrix-sensitivity-legend">
            {groups.map((group) => (
                <span key={group.label}>
                    <i style={{ backgroundColor: getTokenColor(group.label) }} />
                    {group.label}
                </span>
            ))}
        </div>
    );
}

const WeaponMatrixResults: React.FC<WeaponMatrixResultsProps> = ({ output, isLoading = false, placeholder }) => {
    const [compactLayout, setCompactLayout] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 1200 : false));
    const [tabbedLayout, setTabbedLayout] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 768 : false));
    const targets = useMemo(() => output?.targets ?? placeholder?.targets ?? [], [output?.targets, placeholder?.targets]);
    const [activeBpLevel, setActiveBpLevel] = useState<BattlePressureLevel | null>(() => targets[0]?.bpLevel ?? null);
    const loadoutCount = output?.cells[0]?.loadoutOutputs.length ?? placeholder?.loadoutTitles.length ?? 0;
    const groups = output ? getTokenScenarioGroups(output) : [];

    useEffect(() => {
        const handleResize = () => {
            setCompactLayout(window.innerWidth <= 1200);
            setTabbedLayout(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        if (targets.length === 0) {
            if (activeBpLevel !== null) {
                setActiveBpLevel(null);
            }
            return;
        }

        if (!activeBpLevel || !targets.some((target) => target.bpLevel === activeBpLevel)) {
            setActiveBpLevel(targets[0].bpLevel);
        }
    }, [activeBpLevel, targets]);

    if (loadoutCount === 0) {
        return null;
    }

    return (
        <div className="weapon-matrix-results">
            {Array.from({ length: loadoutCount }, (_, loadoutIndex) => {
                const firstOutput = output?.cells[0]?.loadoutOutputs[loadoutIndex];
                const loadoutName = placeholder?.loadoutTitles[loadoutIndex] || formatLoadoutTitle(loadoutIndex, firstOutput?.loadout.name);
                const baseOutput = output ? getLoadoutOutput(getCell(output, 'base', output.targets[0]?.bpLevel ?? 'I'), loadoutIndex) : undefined;
                const visibleTargets =
                    tabbedLayout && activeBpLevel
                        ? targets.filter((target) => target.bpLevel === activeBpLevel)
                        : targets;

                return (
                    <section key={loadoutIndex} className="weapon-matrix-result-section">
                        <h4>{loadoutName}</h4>
                        <div className="weapon-matrix-base-dice">
                            {output ? `Base Full Hit Power Dice = ${simplifyDiceOutput(baseOutput?.diceRolledOnFullHit ?? [])}` : 'Base Full Hit Power Dice ='}
                        </div>
                        {groups.length > 0 ? renderTokenLegend(groups) : null}
                        {tabbedLayout && targets.length > 1 ? (
                            <SegmentedControl
                                ariaLabel={`${loadoutName} battle pressure tabs`}
                                className="weapon-matrix-bp-tabs"
                                fullWidth
                                onChange={setActiveBpLevel}
                                options={targets.map((target) => ({
                                    value: target.bpLevel,
                                    label: `BP ${target.bpLevel}`,
                                }))}
                                size="sm"
                                value={activeBpLevel ?? targets[0].bpLevel}
                            />
                        ) : null}
                        <div className={`weapon-matrix-sensitivity-grid ${tabbedLayout ? 'weapon-matrix-sensitivity-grid-tabbed' : ''}`}>
                            {visibleTargets.map((target) => (
                                <div
                                    key={target.bpLevel}
                                    className={`weapon-matrix-sensitivity-chart ${tabbedLayout ? 'weapon-matrix-sensitivity-chart-tabbed' : ''} ${isLoading ? 'weapon-matrix-sensitivity-chart-loading' : ''}`}
                                >
                                    <div className="weapon-matrix-sensitivity-title">
                                        BP {target.bpLevel} <span>A.T. {getATRangeLabel(target.aeonTrespassValues ?? []) ?? target.aeonTrespassValue}</span>
                                    </div>
                                    <div className="weapon-matrix-sensitivity-canvas">
                                        {output ? (
                                            <Line data={createWoundChanceChartData(output, loadoutIndex, target.bpLevel, groups)} options={createWoundChanceChartOptions(compactLayout)} />
                                        ) : (
                                            <div className="weapon-matrix-placeholder-chart" aria-hidden="true">
                                                <div className="weapon-matrix-placeholder-grid" />
                                                <div className="weapon-matrix-placeholder-lines">
                                                    <span style={{ width: '92%', top: '62%' }} />
                                                    <span style={{ width: '88%', top: '44%' }} />
                                                    <span style={{ width: '90%', top: '29%' }} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {isLoading && (
                                        <div className="weapon-matrix-loading-mask" aria-hidden="true">
                                            <div className="weapon-matrix-loading-spinner" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
};

export default WeaponMatrixResults;
